/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable no-useless-computed-key */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FieldArray, Form, Formik } from "formik";
import Layout from 'components/Layout';
import BrokingSummary from 'components/BrokingSummary';
import Button, { ButtonContainer } from 'components/Button';
import ContentCard from 'components/ContentCard';
import { Col, Row } from 'components/Grid';
import { Label } from "components/Form";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Colors from 'constants/Colors';
import { FitinsurAPI, MessageCenter } from 'utils';
import { GlobalAuthContext, GlobalLoaderContext } from 'store/context';
import { useHistory } from 'react-router-dom';
import { ButtonContent, Line, Scrollbox, Text } from './styles';
import Loader from 'components/Loader';
import Icon from 'components/Icon';
import { LostCancelButton } from 'components/index';
import StepNavigation from 'components/StepNavigation/index';
import RenderButtons from 'components/RenderButtons';
import HasPermission from 'components/HasPermission/index';

export default function PolicyPackApprovalList(props) {
  const [idProduct, setIdProduct] = useState(null);
  const [summary, setSummary] = useState({});
  const [sortList, setSortList] = useState([]);
  const [isEnabledOrderToBind, setIsEnabledOrderToBind] = useState(false);
  const [isEnabledPolicyPackUW, setIsEnabledPolicyPackUW] = useState(false);
  const [showOrderToBindButton, setShowOrderToBindButton] = useState(false);
  const [data, setData] = useState([]);
  const [endorsmentType, setEndorsmentType] = useState("");
  const [filter, setFilter] = useState({
    key: null,
    direction: null,
  });
  const [isBound, setIsBound] = useState(false);

  const formik = useRef(Formik);
  const authContext = useContext(GlobalAuthContext);
  const loading = useContext(GlobalLoaderContext);
  const history = useHistory();
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();

  const coluns = [
    {
      title: "Party",
      value: "nmParty"
    },
    {
      title: "Interest",
      value: "nmInterest"
    },
    {
      title: "Approval Requested",
      value: "approvalRequest"
    },
    {
      title: "Approval Date",
      value: "approvalDate"
    },

    {
      title: "Action",
      value: "action"
    },

  ];

  useMemo(() => {
    setSortList(data);
    if (sortList) {
      sortList.sort((a, b) => {
        if (a[filter.key] < b[filter.key]) {
          return filter.direction === 'asc' ? -1 : 1;
        } else if (a[filter.key] > b[filter.key]) {
          return filter.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
  }, [filter, data]);


  useEffect(() => {
    formik.current.setValues({ insurers: data });
  }, [data])

  useEffect(() => {
    if (authContext.status !== true) {
      mc.error("User is not authenticated, please Login");
      loading.stop();
      goToLoginPage();
      return;
    }
    else {
      fitinsur.setAuthToken(authContext.token);
      loading.stop();
    };
    getGridItems();
    handleSummary();
    setSortList([data]);
    handleOperationInfo();
  }, []);

  useEffect(() => {
    formik.current.setValues({ list: sortList })
  }, [])

  function handleSummary() {
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestHeaderNbi(props.match.params.idOperation)
      .then(response => response.json()).then(data => {
        setSummary({
          typeOfCover: data.ret_value.typeOfCover,
          client: data.ret_value.client,
          obligorCoding: data.ret_value.obligorCoding,
          responseBy: data.ret_value.responseBy,
          internalContact: data.ret_value.internalContact,
          broker: data.ret_value.broker,
          dealCode: data.ret_value.deal,
          nmFacility: data.ret_value.nmFacility,
          currency: data.ret_value.currency,
          nmObligor: data.ret_value.nmObligor,
          projectName: data.ret_value.projectName,
          idOperation: props.match.params.idOperation,
        });
        setEndorsmentType(data.ret_value.endorsmentType);
      })
      .catch((error) => {
        mc.error(`There was an error while fetching your data: ${handleSummary.name}\n` + error);
        console.error('fitPostSummary - Error:', error);
      });
  };

  function getGridItems() {
    loading.start();
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestPolicyPack(props.match.params.idOperation)
      .then(x => x.json())
      .then(res => {
        loading.stop()
        if (res.ret_code !== 200) {
          mc.error(res.ret_msg_details);
          return;
        }
        if (res.ret_value.isEnabledPolicyPackUW) {
          setIsEnabledPolicyPackUW(res.ret_value.isEnabledPolicyPackUW);
        }
        if (res.ret_value.isEnabledOrderToBind) {
          setIsEnabledOrderToBind(res.ret_value.isEnabledOrderToBind);
        }
        if (res.ret_value.idDomainStatus) {
          setShowOrderToBindButton([47, 48, 49, 123, 124, 125, 148, 149, 150, 210, 211, 212].includes(res.ret_value.idDomainStatus));
        }
        if (res.ret_value.approvals && res.ret_value.approvals.length >= 1) {
          let list = res.ret_value.approvals.map(x => {
            return {
              idPersonInsurer: x.idPersonInsurer,
              nmParty: x.party,
              nmInterest: x.interest,
              approvalRequested: x.approvalRequested ? `${new Date(x.approvalRequested).toLocaleDateString()} ${new Date(x.approvalRequested).toLocaleTimeString()}`
                : '-', approvalDate: x.approvalDate ? `${new Date(x.approvalDate).toLocaleDateString()} ${new Date(x.approvalDate).toLocaleTimeString()}`
                  : '-',
              linkAcecss: x?.insurerLink
            }
          })
          setData(list)
        }
      })
  }

  function handleSortList(key) {
    let direction = 'asc';
    if (filter.key === key && filter.direction === 'asc') {
      direction = 'desc';
    }
    setFilter({ key: key, direction: direction });
  }

  async function handleOperationInfo() {
    loading.start()
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestOperationInfo(props.match.params.idOperation)
      .then(response => response.json())
      .then(response => {
        setIsBound(response?.isBound);
        setIdProduct(response?.isDeclaration ? response?.idProduct : 12);

        loading.stop();
      })
      .catch((error) => {
        mc.error(`There was an error while fetching your data: ${handleOperationInfo.name}\n` + error);
        loading.stop();
      });
  }

  useEffect(() => { 
    if(idProduct == 12) { 
      history.push(`/broking/send-approval-pack/${props.match.params.idOperation}`)
    } else if(idProduct == 16) { 
      history.push(`/broking/send-approval-pack-declaration/${props.match.params.idOperation}`)
    }
  }, [idProduct])

  const goToLoginPage = () => {
    history.push('/login');
  };

  const renderFlowClient = () => {
    return (
      <HasPermission permission={props.permissions.POLICY_PACK_SEND_INSURER}>
        <Button
          onClick={() => {
            history.push(`/broking/market-composition/${props.match.params.idOperation}`,
              { idOperation: props.match?.params?.idOperation || null, idProduct: props.location?.state?.idProduct || null })
          }}
          third
          style={{ marginLeft: "10px", marginTop: "5px" }}
        >
          CDR
        </Button>
        <HasPermission permission={!showOrderToBindButton && endorsmentTypeEnum[endorsmentType] && props.permissions.ENDORSEMENT_PACK_SEND_INSURER}>
          <Button
            disabled={!isEnabledPolicyPackUW}
            primary
            style={{ marginLeft: "10px", marginTop: "5px" }}
            onClick={() => { history.push(`/broking/send-policy-pack-insurer/${props.match.params.idOperation}`) }}
          >
            ENDORS. PACK INSURER
          </Button>
        </HasPermission>

        <HasPermission permission={!showOrderToBindButton && !endorsmentTypeEnum[endorsmentType] && props.permissions.POLICY_PACK_SEND_INSURER}>
          <Button
            disabled={!isEnabledPolicyPackUW}
            primary
            style={{ marginLeft: "10px", marginTop: "5px" }}
            onClick={() => { history.push(`/broking/send-policy-pack-insurer/${props.match.params.idOperation}`) }}
          >
            POLICY PACK INSURER
          </Button>
        </HasPermission>

        <HasPermission permission={showOrderToBindButton && props.permissions.ORDER_TO_BIND_GENERATE}>
          <Button
            primary
            disabled={!isEnabledOrderToBind}
            style={{ marginLeft: "10px", marginTop: "5px" }}
            onClick={() => { history.push(`/broking/send-order-to-bind/${props.match.params.idOperation}`) }}
          >
            ORDER TO BIND
          </Button>
        </HasPermission>
      </HasPermission>
    )
  }

  const endorsmentTypeEnum = {
    ['not-pre-agreed']: {
      title: 'Endorsement - Anniversary: Premium Not Pre Agreed / Approvals'
    },
    ['cancellation']: {
      title: 'Endorsement - Cancellation / Approvals'
    },
    ['market-changes']: {
      title: 'Endorsement - Risk & Market Changes / Approvals'
    }
  }

  return (
    <Layout idOperation={props.match?.params?.idOperation} title={summary.projectName ? summary.projectName : summary.nmObligor ? summary.nmObligor : "Broking name"} subtitle={summary.dealCode ? summary.dealCode : "Deal Code"} >
      <StepNavigation stage={2} idOperation={props.match?.params?.idOperation} />
      <BrokingSummary data={summary} />
      <ContentCard title={endorsmentTypeEnum[endorsmentType] ? endorsmentTypeEnum[endorsmentType].title : "APPROVALS"} icon info={{ idOperation: props.match?.params?.idOperation || null, idProduct: props.location?.state?.idProduct || null }}>
        <Row style={{ marginLeft: "0px", marginRight: "0px", justifyContent: "center", alignItems: "center" }}>
          {coluns
            ? coluns.map((colun, index) => (
              <Col key={index} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Text onClick={() => handleSortList(colun.value)} style={{ margin: "0 auto", textTransform: "uppercase" }}>
                  {colun.title}
                </Text>
                {filter.key === colun.value && filter.direction === "asc"
                  ? <FontAwesomeIcon icon={faChevronUp} color={Colors.GRAY[6]} size="1x" />
                  : <FontAwesomeIcon icon={faChevronDown} color={Colors.GRAY[6]} size="1x" />
                }
              </Col>
            ))
            : ""
          }
        </Row>
        <>
          <Formik
            initialValues={{ lock: false, insurers: [], attachments: [], documentType: [] }}
            innerRef={formik}
          >
            {formikProps => (
              <Form>
                <Scrollbox>
                  <FieldArray name='insurers' render={() => (
                    <>
                      {(formikProps.values.insurers && formikProps.values.insurers.length > 0) ? (
                        <>
                          {formikProps.values.insurers.map((item, index) => (
                            <>
                              <Row key={index} style={{ display: "flex", marginLeft: "0px", marginRight: "0px", marginTop: 5, marginBottom: 5, justifyContent: "center", alignItems: "center" }}>

                                <Col style={{ display: "flex", fontWeight: "bold", color: 'gray' }}>
                                  <Text style={{ whiteSpace: "nowrap" }}>{item.nmParty}</Text>
                                </Col>
                                <Col style={{ display: "flex", textAlign: "center", justifyContent: "center", fontWeight: "bold" }}>
                                  <Text style={{ whiteSpace: "nowrap" }}>{item.nmInterest}</Text>
                                </Col>
                                <Col style={{ display: "flex", textAlign: "center", justifyContent: "center", fontWeight: "bold" }}>
                                  <Text style={{ whiteSpace: "nowrap" }}>{item.approvalRequested}</Text>
                                </Col>
                                <Col style={{ display: "flex", textAlign: "center", justifyContent: "center", fontWeight: "bold" }}>
                                  <Text style={{ whiteSpace: "nowrap" }}>{item.approvalDate}</Text>
                                </Col>
                                <Col style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                  <Label>
                                    {isBound ? 
                                      <></>
                                      :
                                      <Icon
                                        style={{ cursor: "pointer" }}
                                        name='edit'
                                        data-debug={JSON.stringify(item)}
                                        onClick={() => {
                                          if (!props.permissions.APPROVE_REJECT_INTERNALLY) return;                                        
                                          else if (item.linkAcecss && item.nmInterest === "SENT" && isEnabledOrderToBind === false) {
                                            window.open(item.linkAcecss.lnkAccess, '_blank');
                                          } else {
                                            history.push(`/broking/approval-policy-pack/${props.match.params.idOperation}/${item.idPersonInsurer}`,
                                             !(index === (formikProps.values.insurers.length - 1)) || item.nmInterest !== 'SENT' ? { hideFooter: true } : {})
                                          }
                                        }}
                                      />
                                    }
                                  </Label>
                                </Col>
                              </Row>
                              <Line />
                            </>
                          ))}
                        </>
                      )
                        : ""
                      }
                    </>
                  )} />

                </Scrollbox>
                <ButtonContainer>
                  <Button forth onClick={() => { history.goBack() }}>&lt; Back</Button>
                  <RenderButtons idOperation={props.match?.params?.idOperation}>
                    <ButtonContent>
                      <div style={{ marginTop: "5px" }}>
                        <LostCancelButton idOperation={props.match?.params?.idOperation} />
                      </div>
                      {isBound ? <></> : renderFlowClient()}
                    </ButtonContent>
                  </RenderButtons>
                </ButtonContainer>
              </Form>
            )}
          </Formik>
          {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
        </>

      </ContentCard>
    </Layout>
  )
}
